/**
 * RematchModal Component
 * Quick rematch button/modal after game ends
 */

import React, { useState } from "react";
import { View, Text, TouchableOpacity, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { GameSession, GameDefinition } from "../types";
import { sendRematchNotification } from "../utils/teamNotifications";
import { useAuth } from "@/providers/AuthProvider";

interface RematchModalProps {
  /** Whether modal is visible */
  visible: boolean;
  /** The completed game session */
  session: GameSession;
  /** Game definition */
  game: GameDefinition;
  /** Opponent user info */
  opponent: {
    id: string;
    full_name: string;
  };
  /** Whether current user won */
  didWin: boolean;
  /** Callback when rematch requested */
  onRematch: () => void;
  /** Callback when close */
  onClose: () => void;
}

/**
 * Modal shown after game ends to request rematch
 */
export const RematchModal: React.FC<RematchModalProps> = ({
  visible,
  session,
  game,
  opponent,
  didWin,
  onRematch,
  onClose,
}) => {
  const { activeProfile } = useAuth();
  const [isSending, setIsSending] = useState(false);
  const [rematchSent, setRematchSent] = useState(false);

  const handleRematch = async () => {
    if (!activeProfile) return;

    setIsSending(true);
    try {
      // Send rematch notification to opponent
      await sendRematchNotification({
        fromUserId: activeProfile.id,
        fromUserName: activeProfile.full_name,
        toUserId: opponent.id,
        gameName: game.name,
        gameSlug: game.slug,
        previousSessionId: session.id,
      });

      setRematchSent(true);
      onRematch();
    } catch (error) {
      console.error("Error sending rematch:", error);
    } finally {
      setIsSending(false);
    }
  };

  if (!visible) return null;

  return (
    <View className="absolute inset-0 bg-black/60 justify-center items-center p-6 z-50">
      <View className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-sm overflow-hidden">
        {/* Result header */}
        <View
          className={`py-6 px-4 items-center ${
            didWin ? "bg-green-500" : "bg-gray-600"
          }`}
        >
          <Ionicons
            name={didWin ? "trophy" : "ribbon"}
            size={48}
            color="#FFFFFF"
          />
          <Text className="text-white text-2xl font-bold mt-2">
            {didWin ? "Je hebt gewonnen! 🎉" : session.result?.winner_user_id ? "Helaas verloren" : "Gelijkspel!"}
          </Text>
          <Text className="text-white/80 mt-1">
            tegen {opponent.full_name}
          </Text>
        </View>

        {/* Body */}
        <View className="p-5">
          {/* Points earned (if any) */}
          {session.points_awarded && session.points_awarded > 0 && didWin && (
            <View className="flex-row items-center justify-center bg-amber-50 dark:bg-amber-900/20 py-3 rounded-xl mb-4">
              <Ionicons name="star" size={24} color="#D97706" />
              <Text className="ml-2 text-lg font-bold text-amber-700 dark:text-amber-400">
                +{session.points_awarded} punten!
              </Text>
            </View>
          )}

          {/* Rematch status */}
          {rematchSent ? (
            <View className="items-center py-4">
              <Ionicons name="checkmark-circle" size={48} color="#10B981" />
              <Text className="mt-2 text-center text-gray-700 dark:text-gray-300 font-medium">
                Rematch verstuurd naar {opponent.full_name}!
              </Text>
              <Text className="mt-1 text-center text-gray-500 dark:text-gray-400 text-sm">
                Je krijgt een melding als ze accepteren
              </Text>
            </View>
          ) : (
            <>
              {/* Game info */}
              <View className="flex-row items-center justify-center mb-4">
                <View
                  className="w-10 h-10 rounded-xl items-center justify-center"
                  style={{ backgroundColor: game.color }}
                >
                  <Ionicons
                    name={game.icon as keyof typeof Ionicons.glyphMap}
                    size={22}
                    color="#FFFFFF"
                  />
                </View>
                <Text className="ml-3 text-lg font-semibold text-gray-800 dark:text-white">
                  {game.name}
                </Text>
              </View>

              <Text className="text-center text-gray-600 dark:text-gray-400 mb-4">
                Wil je nog een keer spelen tegen {opponent.full_name}?
              </Text>
            </>
          )}
        </View>

        {/* Actions */}
        <View className="flex-row p-4 border-t border-gray-100 dark:border-gray-700">
          <TouchableOpacity
            onPress={onClose}
            className="flex-1 py-3 mr-2 items-center rounded-xl bg-gray-100 dark:bg-gray-700"
            accessibilityLabel="Sluiten"
            accessibilityRole="button"
          >
            <Text className="font-medium text-gray-700 dark:text-gray-300">
              {rematchSent ? "Sluiten" : "Nee, bedankt"}
            </Text>
          </TouchableOpacity>

          {!rematchSent && (
            <TouchableOpacity
              onPress={handleRematch}
              disabled={isSending}
              className={`flex-1 py-3 ml-2 items-center rounded-xl flex-row justify-center ${
                isSending ? "bg-purple-400" : "bg-purple-600"
              }`}
              accessibilityLabel="Rematch aanvragen"
              accessibilityRole="button"
            >
              {isSending ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <>
                  <Ionicons name="refresh" size={20} color="#FFFFFF" />
                  <Text className="ml-2 font-bold text-white">Rematch!</Text>
                </>
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
};

export default RematchModal;

